import React, { useState, useEffect } from 'react'
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Grid,
    Card,
    CardActionArea,
    Button,
    Chip,
    useTheme,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    InputLabel,
    FormControl
} from '@mui/material'
import {
    Build as HardwareIcon,
    Window as ShutterIcon,
    GridOn as ScreenIcon,
    Inventory2 as RollerIcon,
    Check as CheckIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon
} from '@mui/icons-material'
import { useLanguage } from '../../Commun/contexts/LanguageContext'
import { componentsApi } from '../services/componentsApi'
import { useSystemParameters } from '../../Commun/contexts/SystemParametersContext'

export interface ComponentItem {
    id: string
    name: string
    category: 'hardware' | 'shutter' | 'screen' | 'roller'
    icon: string
    description: string
    unitPrice: number
    defaultQuantity?: number
}

interface ComponentLibraryProps {
    onAddComponent: (component: ComponentItem) => void
    selectedComponents?: ComponentItem[]
    hideAddButton?: boolean
    hideActions?: boolean
    onEditComponent?: (component: ComponentItem) => void
}

const ComponentLibrary: React.FC<ComponentLibraryProps> = ({ onAddComponent, selectedComponents = [], hideAddButton = false, hideActions = false, onEditComponent }) => {
    const [activeCategory, setActiveCategory] = useState<'hardware' | 'shutter' | 'screen' | 'roller'>('hardware')
    const [components, setComponents] = useState<ComponentItem[]>([])
    const [loading, setLoading] = useState(false)
    const [openDialog, setOpenDialog] = useState(false)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [componentToDelete, setComponentToDelete] = useState<string | null>(null)
    const [editingComponent, setEditingComponent] = useState<ComponentItem | null>(null)
    const [formData, setFormData] = useState<Partial<ComponentItem>>({ category: 'hardware', icon: '🔧' })

    const theme = useTheme();
    const { t } = useLanguage();
    const { getParam } = useSystemParameters();
    const currencySymbol = getParam('Currency_Symbol', 'DZD');

    const fetchComponents = async () => {
        setLoading(true)
        const result = await componentsApi.getAll()
        if (result.success) {
            setComponents(result.data)
        }
        setLoading(false)
    }

    const dataFetchedRef = React.useRef(false);

    useEffect(() => {
        if (dataFetchedRef.current) return;
        dataFetchedRef.current = true;
        fetchComponents()
    }, [])

    const handleSave = async () => {
        if (!formData.name || !formData.unitPrice) return

        if (editingComponent) {
            await componentsApi.update({ ...editingComponent, ...formData } as ComponentItem)
        } else {
            await componentsApi.create(formData as ComponentItem)
        }
        setOpenDialog(false)
        fetchComponents()
        setEditingComponent(null)
        setFormData({ category: 'hardware', icon: '🔧' })
    }

    const handleDeleteClick = (id: string, e: React.MouseEvent) => {
        e.stopPropagation()
        setComponentToDelete(id)
        setDeleteDialogOpen(true)
    }

    const confirmDelete = async () => {
        if (componentToDelete) {
            await componentsApi.delete(componentToDelete)
            fetchComponents()
            setDeleteDialogOpen(false)
            setComponentToDelete(null)
        }
    }

    const handleEdit = (component: ComponentItem, e: React.MouseEvent) => {
        e.stopPropagation()
        if (onEditComponent) {
            onEditComponent(component)
        } else {
            // Legacy internal behavior (fallback)
            setEditingComponent(component)
            setFormData(component)
            setOpenDialog(true)
        }
    }

    const handleOpenAdd = () => {
        setEditingComponent(null)
        setFormData({ category: activeCategory, icon: '🔧', unitPrice: 0, defaultQuantity: 1 })
        setOpenDialog(true)
    }

    const filteredComponents = components.filter(c => c.category === activeCategory)

    return (
        <Box sx={{ p: 0 }}>


            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                    value={activeCategory}
                    onChange={(_, val) => setActiveCategory(val)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab icon={<HardwareIcon />} label={t('maker.design.components.cat.hardware')} value="hardware" iconPosition="start" />
                    <Tab icon={<ShutterIcon />} label={t('maker.design.components.cat.shutter')} value="shutter" iconPosition="start" />
                    <Tab icon={<ScreenIcon />} label={t('maker.design.components.cat.screen')} value="screen" iconPosition="start" />
                    <Tab icon={<RollerIcon />} label={t('maker.design.components.cat.roller')} value="roller" iconPosition="start" />
                </Tabs>
            </Box>

            <Grid container spacing={2}>
                {filteredComponents.map((component) => {
                    const isSelected = selectedComponents.some(c => c.id === component.id)
                    return (
                        <Grid item xs={12} sm={6} md={4} key={component.id}>
                            <Card
                                variant={isSelected ? "outlined" : "elevation"}
                                elevation={isSelected ? 0 : 1}
                                sx={{
                                    height: '100%',
                                    position: 'relative',
                                    transition: 'all 0.2s',
                                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : undefined,
                                    bgcolor: isSelected ? 'action.selected' : 'background.paper',
                                    '&:hover .actions': { opacity: 1 }
                                }}
                            >
                                <CardActionArea onClick={() => onAddComponent(component)} sx={{ height: '100%', p: 2, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start' }}>
                                    <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Box sx={{ fontSize: '2rem' }}>{component.icon}</Box>
                                        <Typography variant="h6" color="primary" fontWeight="bold">
                                            ${component.unitPrice.toFixed(2)}
                                        </Typography>
                                    </Box>

                                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                        {component.name}
                                    </Typography>

                                    <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1, mb: 1 }}>
                                        {component.description}
                                    </Typography>

                                    {component.defaultQuantity && component.defaultQuantity > 1 && (
                                        <Chip label={t('maker.design.components.pool', component.defaultQuantity)} size="small" variant="outlined" sx={{ mt: 1 }} />
                                    )}

                                    {isSelected && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                color: 'primary.main',
                                                bgcolor: 'background.paper',
                                                borderRadius: '50%',
                                                p: 0.5
                                            }}
                                        >
                                            <CheckIcon />
                                        </Box>
                                    )}
                                </CardActionArea>
                                {!hideActions && (
                                    <Box className="actions" sx={{ position: 'absolute', bottom: 5, right: 5, opacity: 0, transition: 'opacity 0.2s' }}>
                                        <IconButton size="small" onClick={(e) => handleEdit(component, e)}><EditIcon fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error" onClick={(e) => handleDeleteClick(component.id, e)}><DeleteIcon fontSize="small" /></IconButton>
                                    </Box>
                                )}
                            </Card>
                        </Grid>
                    )
                })}
            </Grid>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">{t('common.confirm.title.delete')}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t('common.confirm.delete')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>{t('btn.cancel')}</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained" autoFocus>{t('common.actions.confirm')}</Button>
                </DialogActions>
            </Dialog>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
                <DialogTitle>{editingComponent ? t('common.actions.edit') : t('common.actions.new')}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label={t('maker.field.name')}
                                fullWidth
                                value={formData.name || ''}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label={t('maker.field.description')}
                                fullWidth
                                value={formData.description || ''}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label={t('maker.field.unitPrice')}
                                type="number"
                                fullWidth
                                value={formData.unitPrice || 0}
                                onChange={(e) => setFormData({ ...formData, unitPrice: parseFloat(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Quantity"
                                type="number"
                                fullWidth
                                value={formData.defaultQuantity || 1}
                                onChange={(e) => setFormData({ ...formData, defaultQuantity: parseInt(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Type</InputLabel>
                                <Select
                                    value={formData.category || 'hardware'}
                                    label="Type"
                                    onChange={(e) => setFormData({ ...formData, category: e.target.value as any })}
                                >
                                    <MenuItem value="hardware">Hardware</MenuItem>
                                    <MenuItem value="shutter">Shutter</MenuItem>
                                    <MenuItem value="screen">Screen</MenuItem>
                                    <MenuItem value="roller">Roller</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>{t('common.actions.cancel')}</Button>
                    <Button onClick={handleSave} variant="contained">{t('common.actions.save')}</Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}

export default ComponentLibrary
